
// src/composables/useValidation.ts
import { ref, computed } from 'vue'
import api from '@/plugins/axios'

type FieldsMap = Record<string, string>

/**
 * Was DRF bei einer 400-Antwort schickt: pro Feldname die Liste der Meldungen.
 * Der Schluessel ist der Backend-Name, nicht der des Formularfeldes — die
 * Uebersetzung dazwischen macht `fields`.
 */
export type ValidationErrors = Record<string, string[]>

/**
 * Ein Formularzustand, wie ihn die Masken hin- und herreichen. Die Werte sind
 * `unknown`, weil hier alles durchlaeuft, was in einem Eingabefeld stehen kann
 * — Zahl, Zeichenkette, Auswahl, leer; interpretiert wird es erst dort, wo es
 * hingehoert.
 */
export type FieldValues = Record<string, unknown>

/**
 * Vue 3 composable equivalent of your ValidationMixin.
 * - Debounced POST to validationUrl
 * - Tracks fieldErrors (mapped by fields)
 * - Exposes valid state once something is touched and no errors remain.
 */
export function useValidation(validationUrl: string, fields: FieldsMap) {
  const touchedFields = ref<string[]>([])
  const newlyTouchedFields = ref<string[]>([])
  const validationErrors = ref<ValidationErrors>({})
  const validationTimer = ref<number | null>(null)

  const fieldErrors = computed(() => {
  const errors: Record<string, string[] | undefined> = {}
  for (const field of touchedFields.value) {
    const backendKey = fields[field as keyof typeof fields]
    if (backendKey !== undefined) {
      errors[field] = validationErrors.value[backendKey]
    }
  }
  return errors
  })

  const valid = computed(() => {
    return (
      Object.keys(validationErrors.value).length === 0 &&
      !validationTimer.value &&
      touchedFields.value.length > 0
    )
  })

  function validate(data: FieldValues) {
    if (validationTimer.value) {
      clearTimeout(validationTimer.value)
    }
    validationTimer.value = window.setTimeout(async () => {
      validationTimer.value = null
      try {
        await api.post(validationUrl, data)
        validationErrors.value = {}
      } catch (error) {
        const failure = error as {
          response?: { status?: number; data?: ValidationErrors }
        }
        if (failure?.response?.status === 400) {
          validationErrors.value = failure.response.data ?? {}
        }
      }
    }, 600)
  }

  function getDataFromFields(stateObj: FieldValues) {
  const data: FieldValues = {}

  for (const frontend of Object.keys(fields) as Array<keyof typeof fields>) {
    const backend = fields[frontend] // backend is string | undefined
    if (backend === undefined) continue // guard for safety

    const fieldValue = stateObj[frontend as string]
    data[backend] = fieldValue

    if (fieldValue !== null && fieldValue !== undefined && fieldValue !== '') {
      newlyTouchedFields.value = [...newlyTouchedFields.value, frontend as string]
    }
  }
  return data
}


  function setFieldsFromData(targetState: FieldValues, incoming: unknown) {
  if (incoming && typeof incoming === 'object') {
    const source = incoming as FieldValues
    for (const frontend of Object.keys(fields) as Array<keyof typeof fields>) {
      const backend = fields[frontend]
      if (backend === undefined) continue

      const fieldValue = source[backend]
      targetState[frontend as string] = fieldValue

      if (fieldValue !== null && fieldValue !== undefined && fieldValue !== '') {
        newlyTouchedFields.value = [...newlyTouchedFields.value, frontend as string]
      }
    }
  }
}


  // merge newly touched into touched on validationErrors change
  function mergeTouched() {
    touchedFields.value = Array.from(
      new Set([...touchedFields.value, ...newlyTouchedFields.value]),
    )
    newlyTouchedFields.value = []
  }

  return {
    fieldErrors,
    valid,
    validate,
    getDataFromFields,
    setFieldsFromData,
    validationErrors,
    touchedFields,
    newlyTouchedFields,
    mergeTouched,
  }
}
